import axios from "axios";

import { BASE_URL } from "./apiBase";

const authHeaders = (token) => {
  const bearerToken =
    typeof token === "string" && token.trim() ? token.trim() : null;
  return bearerToken ? { Authorization: `Bearer ${bearerToken}` } : {};
};

// ─── Demo content guard ──────────────────────────────────────────────────────
// ProductionCatalogSeeder wrote its demo catalog to reserved high IDs: farms
// 1001-1025, products 11001-11130, baskets 12001-12065. The RemoveDemoCatalog
// migration deletes those rows, but it only takes effect once the backend
// actually redeploys. Until then the storefront must not show them, so every
// read is filtered here — the single boundary all storefront calls pass through.
//
// adminService is deliberately NOT filtered, so admin tooling continues to show
// the real contents of the tables.
//
// Safe to delete this block once /api/settings/public responds (which proves the
// gated build is live) and /api/Farms returns an empty list.
const DEMO_ID_RANGES = {
  farm: [1001, 1025],
  product: [11001, 11130],
  basket: [12001, 12065],
};

const isDemoId = (kind, id) => {
  const [lo, hi] = DEMO_ID_RANGES[kind];
  const numeric = Number(id);
  return Number.isFinite(numeric) && numeric >= lo && numeric <= hi;
};

/** Drop demo records from a list response. Non-arrays pass through untouched. */
const withoutDemo = (kind, data) =>
  Array.isArray(data) ? data.filter((row) => !isDemoId(kind, row?.id)) : data;

/** Treat a demo record fetched by id as not found, so detail pages 404 cleanly. */
const rejectDemo = (kind, data) =>
  data && isDemoId(kind, data.id) ? null : data;

// ─── Farms ───────────────────────────────────────────────────────────────────
export const getFarms = async () => {
  const response = await axios.get(`${BASE_URL}/api/Farms`);
  return withoutDemo("farm", response.data.items ?? response.data);
};

export const getFarmById = async (id) => {
  const response = await axios.get(`${BASE_URL}/api/Farms/${id}`);
  return rejectDemo("farm", response.data);
};

// ─── Baskets ─────────────────────────────────────────────────────────────────
export const getBaskets = async (farmId, { pageSize } = {}) => {
  const params = {};
  if (farmId) params.farmId = farmId;
  if (pageSize) params.pageSize = pageSize;
  const response = await axios.get(`${BASE_URL}/api/Baskets`, { params });
  return withoutDemo("basket", response.data.items ?? response.data);
};

export const getBasketById = async (id) => {
  const response = await axios.get(`${BASE_URL}/api/Baskets/${id}`);
  return rejectDemo("basket", response.data);
};

// ─── Products ────────────────────────────────────────────────────────────────
export const getProducts = async (farmId, { search, page, pageSize } = {}) => {
  const params = {};
  if (farmId) params.farmId = farmId;
  if (search) params.search = search;
  if (page) params.page = page;
  if (pageSize) params.pageSize = pageSize;
  const response = await axios.get(`${BASE_URL}/api/Products`, { params });
  return withoutDemo("product", response.data.items ?? response.data);
};

export const getProductById = async (id) => {
  const response = await axios.get(`${BASE_URL}/api/Products/${id}`);
  return rejectDemo("product", response.data);
};

// ─── Orders ──────────────────────────────────────────────────────────────────
export const createOrder = async (token, payload) => {
  const response = await axios.post(`${BASE_URL}/api/Orders`, payload, {
    headers: authHeaders(token),
  });
  return response.data;
};

export const getMyOrders = async (token) => {
  const response = await axios.get(`${BASE_URL}/api/Orders/me`, {
    headers: authHeaders(token),
  });
  return response.data.items ?? response.data;
};

export const getOrderById = async (token, id) => {
  const response = await axios.get(`${BASE_URL}/api/Orders/${id}`, {
    headers: authHeaders(token),
  });
  return response.data;
};

export const cancelOrder = async (token, id) => {
  const response = await axios.patch(
    `${BASE_URL}/api/Orders/${id}/cancel`,
    {},
    { headers: authHeaders(token) }
  );
  return response.data;
};

// ─── Reviews ─────────────────────────────────────────────────────────────────
export const getFarmReviews = async (farmId) => {
  const response = await axios.get(`${BASE_URL}/api/Reviews/farm/${farmId}`);
  return response.data;
};

export const getFarmReviewSummary = async (farmId) => {
  const response = await axios.get(
    `${BASE_URL}/api/Reviews/farm/${farmId}/summary`
  );
  return response.data;
};

export const createReview = async (token, data) => {
  const response = await axios.post(`${BASE_URL}/api/Reviews`, data, {
    headers: authHeaders(token),
  });
  return response.data;
};

export const deleteReview = async (token, id) => {
  await axios.delete(`${BASE_URL}/api/Reviews/${id}`, {
    headers: authHeaders(token),
  });
};

// ─── Wishlist ─────────────────────────────────────────────────────────────────
export const getWishlist = async (token) => {
  const response = await axios.get(`${BASE_URL}/api/Wishlist`, {
    headers: authHeaders(token),
  });
  return response.data;
};

export const getSavedFarmIds = async (token) => {
  const response = await axios.get(`${BASE_URL}/api/Wishlist/ids`, {
    headers: authHeaders(token),
  });
  return response.data;
};

export const toggleWishlist = async (token, farmId) => {
  const response = await axios.post(
    `${BASE_URL}/api/Wishlist/${farmId}`,
    {},
    { headers: authHeaders(token) }
  );
  return response.data;
};

// ─── Promo Codes ──────────────────────────────────────────────────────────────
export const validatePromoCode = async (token, code, orderAmount) => {
  const response = await axios.post(
    `${BASE_URL}/api/PromoCodes/validate`,
    { code, orderAmount },
    { headers: authHeaders(token) }
  );
  return response.data;
};

// ─── Villager Farm Registration ──────────────────────────────────────────────
export const registerFarm = async (data) => {
  const response = await axios.post(
    `${BASE_URL}/api/Villager/register-farm`,
    data
  );
  return response.data;
};

export const updateMyFarm = async (data) => {
  const response = await axios.put(
    `${BASE_URL}/api/Villager/my-farm`,
    data
  );
  return response.data;
};

// ─── Villager Portal ─────────────────────────────────────────────────────────
export const getVillagerFarm = async (token) => {
  const response = await axios.get(`${BASE_URL}/api/Villager/my-farm`, {
    headers: authHeaders(token),
  });
  return response.data;
};

export const getVillagerOrders = async (token) => {
  const response = await axios.get(`${BASE_URL}/api/Villager/my-farm/orders`, {
    headers: authHeaders(token),
  });
  return response.data;
};

/**
 * The farm's own baskets, including availableQuantity — how many the current
 * ingredient stock can build. The endpoint existed from the start but nothing
 * called it, so a farmer could not see an unbuildable basket anywhere in their
 * portal; they found out from a customer.
 */
export const getVillagerBaskets = async (token) => {
  const response = await axios.get(`${BASE_URL}/api/Villager/my-farm/baskets`, {
    headers: authHeaders(token),
  });
  return response.data;
};

/**
 * Basket CRUD for the farm's owner. The endpoints shipped with the portal but
 * nothing in the app called them, so a farmer had to ask an admin to assemble a
 * basket for them. `items` is [{ productId, quantity }].
 */
export const createVillagerBasket = async (token, data) => {
  const response = await axios.post(`${BASE_URL}/api/Villager/my-farm/baskets`, data, {
    headers: authHeaders(token),
  });
  return response.data;
};

export const updateVillagerBasket = async (token, basketId, data) => {
  const response = await axios.put(
    `${BASE_URL}/api/Villager/my-farm/baskets/${basketId}`,
    data,
    { headers: authHeaders(token) }
  );
  return response.data;
};

/** Deletes, or deactivates instead when the basket already appears in an order. */
export const deleteVillagerBasket = async (token, basketId) => {
  const response = await axios.delete(
    `${BASE_URL}/api/Villager/my-farm/baskets/${basketId}`,
    { headers: authHeaders(token) }
  );
  return response.data;
};

export const getVillagerStats = async (token) => {
  const response = await axios.get(`${BASE_URL}/api/Villager/my-farm/stats`, {
    headers: authHeaders(token),
  });
  return response.data;
};

export const updateProductStock = async (token, productId, stock) => {
  const response = await axios.patch(
    `${BASE_URL}/api/Villager/my-farm/products/${productId}/stock`,
    { stock },
    { headers: authHeaders(token) }
  );
  return response.data;
};

export const createProduct = async (token, data) => {
  const response = await axios.post(
    `${BASE_URL}/api/Villager/my-farm/products`,
    data,
    { headers: authHeaders(token) }
  );
  return response.data;
};

export const updateProduct = async (token, productId, data) => {
  const response = await axios.put(
    `${BASE_URL}/api/Villager/my-farm/products/${productId}`,
    data,
    { headers: authHeaders(token) }
  );
  return response.data;
};

export const toggleProductActive = async (token, productId) => {
  const response = await axios.patch(
    `${BASE_URL}/api/Villager/my-farm/products/${productId}/toggle`,
    {},
    { headers: authHeaders(token) }
  );
  return response.data;
};

export const deleteProduct = async (token, productId) => {
  const response = await axios.delete(
    `${BASE_URL}/api/Villager/my-farm/products/${productId}`,
    { headers: authHeaders(token) }
  );
  return response.data;
};

export const bulkUpdateProducts = async (token, updates) => {
  const response = await axios.patch(
    `${BASE_URL}/api/Villager/my-farm/products/bulk`,
    { updates },
    { headers: authHeaders(token) }
  );
  return response.data;
};

export const getVillagerEarnings = async (token) => {
  const response = await axios.get(
    `${BASE_URL}/api/Villager/my-farm/earnings`,
    { headers: authHeaders(token) }
  );
  return response.data;
};

export const assignDelivery = async (token, orderId, assignment) => {
  const response = await axios.patch(
    `${BASE_URL}/api/Villager/my-farm/orders/${orderId}/assign`,
    { assignment },
    { headers: authHeaders(token) }
  );
  return response.data;
};

export const acknowledgeOrder = async (token, orderId) => {
  const response = await axios.patch(
    `${BASE_URL}/api/Villager/my-farm/orders/${orderId}/acknowledge`,
    {},
    { headers: authHeaders(token) }
  );
  return response.data;
};

export const updateOrderStatus = async (token, orderId, status) => {
  const response = await axios.patch(
    `${BASE_URL}/api/Villager/my-farm/orders/${orderId}/status`,
    { status },
    { headers: authHeaders(token) }
  );
  return response.data;
};

// ─── Subscriptions ──────────────────────────────────────────────────────────
export const getSubscriptionPlans = async (token) => {
  const response = await axios.get(
    `${BASE_URL}/api/Villager/my-farm/subscriptions`,
    { headers: authHeaders(token) }
  );
  return response.data;
};

export const createSubscriptionPlan = async (token, data) => {
  const response = await axios.post(
    `${BASE_URL}/api/Villager/my-farm/subscriptions`,
    data,
    { headers: authHeaders(token) }
  );
  return response.data;
};

export const updateSubscriptionPlan = async (token, planId, data) => {
  await axios.put(
    `${BASE_URL}/api/Villager/my-farm/subscriptions/${planId}`,
    data,
    { headers: authHeaders(token) }
  );
};

export const curateSubscriptionBox = async (token, planId, items) => {
  await axios.put(
    `${BASE_URL}/api/Villager/my-farm/subscriptions/${planId}/box`,
    { items },
    { headers: authHeaders(token) }
  );
};

export const addSubscriptionBlackout = async (token, planId, blackoutDate) => {
  await axios.post(
    `${BASE_URL}/api/Villager/my-farm/subscriptions/${planId}/blackouts`,
    { blackoutDate },
    { headers: authHeaders(token) }
  );
};

export const removeSubscriptionBlackout = async (token, planId, date) => {
  await axios.delete(
    `${BASE_URL}/api/Villager/my-farm/subscriptions/${planId}/blackouts`,
    { headers: authHeaders(token), params: { date } }
  );
};

export const deleteSubscriptionPlan = async (token, planId) => {
  await axios.delete(
    `${BASE_URL}/api/Villager/my-farm/subscriptions/${planId}`,
    { headers: authHeaders(token) }
  );
};

// ─── Pick/Pack Lists ────────────────────────────────────────────────────────
export const getPickList = async (token, date) => {
  const response = await axios.get(
    `${BASE_URL}/api/Villager/my-farm/pick-list`,
    { headers: authHeaders(token), params: date ? { date } : {} }
  );
  return response.data;
};

export const getPackLists = async (token, date) => {
  const response = await axios.get(
    `${BASE_URL}/api/Villager/my-farm/pack-lists`,
    { headers: authHeaders(token), params: date ? { date } : {} }
  );
  return response.data;
};

export const adjustOrderItemWeight = async (token, orderId, orderItemId, actualWeight) => {
  const response = await axios.patch(
    `${BASE_URL}/api/Villager/my-farm/orders/${orderId}/weight`,
    { orderItemId, actualWeight },
    { headers: authHeaders(token) }
  );
  return response.data;
};

// ─── Delivery Photo ─────────────────────────────────────────────────────────
export const uploadDeliveryPhoto = async (token, orderId, photoUrl, markDelivered = true) => {
  const response = await axios.patch(
    `${BASE_URL}/api/Villager/my-farm/orders/${orderId}/delivery-photo`,
    { photoUrl, markDelivered },
    { headers: authHeaders(token) }
  );
  return response.data;
};

export const uploadImage = async (token, file, folder = "delivery") => {
  const formData = new FormData();
  formData.append("file", file);
  formData.append("folder", folder);
  const response = await axios.post(`${BASE_URL}/api/Images/upload`, formData, {
    headers: { ...authHeaders(token), "Content-Type": "multipart/form-data" },
    withCredentials: true,
  });
  return response.data;
};

// ─── Store Credits ──────────────────────────────────────────────────────────
export const getMyCredits = async (token) => {
  const response = await axios.get(`${BASE_URL}/api/StoreCredits/me`, {
    headers: authHeaders(token),
  });
  return response.data;
};

export const getFarmCreditBalance = async (token, farmId) => {
  const response = await axios.get(
    `${BASE_URL}/api/StoreCredits/me/${farmId}`,
    { headers: authHeaders(token) }
  );
  return response.data;
};

export const purchaseCredit = async (token, farmId, amount) => {
  const response = await axios.post(
    `${BASE_URL}/api/StoreCredits/purchase`,
    { farmId, amount },
    { headers: authHeaders(token) }
  );
  return response.data;
};

export const sendGiftCard = async (token, farmId, amount, recipientEmail, message) => {
  const response = await axios.post(
    `${BASE_URL}/api/StoreCredits/gift`,
    { farmId, amount, recipientEmail, message },
    { headers: authHeaders(token) }
  );
  return response.data;
};

export const redeemCredit = async (token, farmId, amount) => {
  const response = await axios.post(
    `${BASE_URL}/api/StoreCredits/redeem`,
    { farmId, amount },
    { headers: authHeaders(token) }
  );
  return response.data;
};

// ─── Customer Subscriptions ─────────────────────────────────────────────────
export const getFarmSubscriptions = async (farmId) => {
  const response = await axios.get(
    `${BASE_URL}/api/Farms/${farmId}/subscriptions`
  );
  return response.data;
};

export const getMySubscriptions = async (token) => {
  const response = await axios.get(
    `${BASE_URL}/api/Subscriptions/me`,
    { headers: authHeaders(token) }
  );
  return response.data;
};

export const subscribeToPlan = async (token, planId) => {
  const response = await axios.post(
    `${BASE_URL}/api/Subscriptions/${planId}`,
    {},
    { headers: authHeaders(token) }
  );
  return response.data;
};

export const toggleSkipSubscription = async (token, subscriptionId) => {
  const response = await axios.patch(
    `${BASE_URL}/api/Subscriptions/${subscriptionId}/skip`,
    {},
    { headers: authHeaders(token) }
  );
  return response.data;
};

export const pauseSubscription = async (token, subscriptionId) => {
  const response = await axios.patch(
    `${BASE_URL}/api/Subscriptions/${subscriptionId}/pause`,
    {},
    { headers: authHeaders(token) }
  );
  return response.data;
};

export const resumeSubscription = async (token, subscriptionId) => {
  const response = await axios.patch(
    `${BASE_URL}/api/Subscriptions/${subscriptionId}/resume`,
    {},
    { headers: authHeaders(token) }
  );
  return response.data;
};

export const cancelSubscription = async (token, subscriptionId) => {
  const response = await axios.patch(
    `${BASE_URL}/api/Subscriptions/${subscriptionId}/cancel`,
    {},
    { headers: authHeaders(token) }
  );
  return response.data;
};

// ─── Farmer Profile ─────────────────────────────────────────────────────────
export const getFarmerTags = async () => {
  const response = await axios.get(`${BASE_URL}/api/Farms/farmer-tags`);
  return response.data;
};

export const getFarmerProfile = async (token) => {
  const response = await axios.get(`${BASE_URL}/api/Villager/farmer-profile`, {
    headers: authHeaders(token),
  });
  return response.data;
};

export const updateFarmerProfile = async (token, profileData) => {
  const response = await axios.put(
    `${BASE_URL}/api/Villager/farmer-profile`,
    profileData,
    { headers: authHeaders(token) }
  );
  return response.data;
};

// ─── Public store settings ───────────────────────────────────────────────────
/** Pricing config the storefront needs (delivery fee, free-delivery threshold,
 *  minimum order). Anonymous-readable allow-list, not the full admin table. */
export const getPublicSettings = async () => {
  const response = await axios.get(`${BASE_URL}/api/settings/public`);
  return response.data;
};

// ─── Contact form ────────────────────────────────────────────────────────────
/** The backend stores the enquiry and queues the email, so this returns as soon as the row is
 *  written. The timeout is a floor against a wedged proxy, not against mail delivery. */
export const sendContactMessage = async ({ name, email, message, farmId }) => {
  const response = await axios.post(
    `${BASE_URL}/api/Contact`,
    { name, email, message, farmId: farmId ? Number(farmId) : null },
    { timeout: 20000 }
  );
  return response.data;
};

// ─── Newsletter (Buletini) ───────────────────────────────────────────────────
/** Opt-in to the newsletter. Idempotent server-side: re-subscribing the same address is a no-op
 *  rather than a duplicate row. */
export const subscribeToNewsletter = async ({ email, language, source }) => {
  const response = await axios.post(
    `${BASE_URL}/api/Newsletter/subscribe`,
    { email, language, source },
    { timeout: 20000 }
  );
  return response.data;
};
